/**
 * form-kit — the one form vocabulary shared by every create/edit surface.
 *
 * The app's screens all speak the same visual language: glass card,
 * uppercase eyebrow labels, 42px/radius-12 fields that turn brand-blue on focus,
 * gradient primary button. Until now each screen re-declared those constants inline,
 * so a dialog written with plain shadcn defaults (`h-9` inputs, sentence-case labels)
 * read as a different product than the page that opened it. This module is the
 * single definition — import it instead of copying the styles again.
 */
import * as React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { DayPickerProps } from 'react-day-picker';
import { brandColors, brandRgba, brandShadowRgba } from '@shared/constants';
import { formatDateInLocale } from '@infra/format';
import { DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { Calendar } from './calendar';
import { Popover, PopoverTrigger, PopoverContent } from './popover';

const BRAND = brandColors.brandPrimary;
const BRAND_2 = brandColors.brandPrimaryAlt;
const BORDER = brandRgba('textPrimary', 0.1);
const TEXT_1 = brandColors.textPrimary;
const TEXT_2 = brandColors.textSecondary;
const TEXT_3 = brandColors.textTertiary;
const WHITE = brandColors.surfaceCard;
const SURFACE = brandColors.surface;
const DANGER = brandColors.danger;
const BORDER_STRONG = brandColors.borderStrong;
const GRADIENT = `linear-gradient(135deg,${BRAND} 0%,${BRAND_2} 100%)`;

/** Raw values, for the rare case a screen needs to style something the kit has no component for. */
export const formTokens = {
  brand: BRAND,
  brand2: BRAND_2,
  gradient: GRADIENT,
  border: BORDER,
  borderStrong: BORDER_STRONG,
  surface: SURFACE,
  white: WHITE,
  text1: TEXT_1,
  text2: TEXT_2,
  text3: TEXT_3,
  danger: DANGER,
  success: brandColors.success,
  fieldHeight: 44,
  fieldRadius: 12,
  cardRadius: 22,
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: TEXT_3,
  } as React.CSSProperties,
  card: {
    background: brandRgba('surfaceCard', 0.96),
    border: `1px solid ${BORDER}`,
    borderRadius: 22,
    boxShadow: `0 1px 2px ${brandShadowRgba('textPrimary', 0.05)}, 0 10px 28px ${brandShadowRgba('brandPrimary', 0.1)}`,
    backdropFilter: 'blur(20px) saturate(180%)',
  } as React.CSSProperties,
} as const;

// Metrics follow the persona-create flow (44px tall, 1.5px resting border) — the reference
// this kit was aligned to. The scenario-create screens still use their own 42px/1px inline
// styles until they are migrated onto the kit.
const fieldBase: React.CSSProperties = {
  width: '100%',
  borderRadius: formTokens.fieldRadius,
  border: `1.5px solid ${BORDER_STRONG}`,
  fontSize: 14.5,
  fontWeight: 600,
  color: TEXT_1,
  background: WHITE,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  transition: 'border-color .18s ease',
};

/** Border colour is driven imperatively (as the inline-styled screens do) so no stylesheet is needed. */
function focusHandlers<T extends HTMLElement>(
  invalid: boolean | undefined,
  onFocus?: React.FocusEventHandler<T>,
  onBlur?: React.FocusEventHandler<T>
) {
  const resting = invalid ? DANGER : BORDER_STRONG;
  return {
    onFocus: (e: React.FocusEvent<T>) => {
      e.currentTarget.style.borderColor = invalid ? DANGER : BRAND;
      onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent<T>) => {
      e.currentTarget.style.borderColor = resting;
      onBlur?.(e);
    },
  };
}

export function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} style={{ ...formTokens.eyebrow, display: 'block', marginBottom: 8 }}>
      {children}
      {required && <span style={{ color: DANGER, marginLeft: 2 }}>*</span>}
    </label>
  );
}

/** Label + control + optional hint|error, with the spacing every screen uses between fields. */
export function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  children,
}: {
  label: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <FieldLabel htmlFor={htmlFor} required={required}>
        {label}
      </FieldLabel>
      {children}
      {hint && (
        <p style={{ margin: '6px 0 0', fontSize: 12, fontWeight: 500, color: TEXT_3 }}>{hint}</p>
      )}
      {error && (
        <p style={{ margin: '6px 0 0', fontSize: 12, fontWeight: 600, color: DANGER }}>{error}</p>
      )}
    </div>
  );
}

export type TextFieldProps = React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean };

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ invalid, style, onFocus, onBlur, ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      style={{
        ...fieldBase,
        height: formTokens.fieldHeight,
        padding: '0 14px',
        borderColor: invalid ? DANGER : BORDER_STRONG,
        ...style,
      }}
      {...focusHandlers<HTMLInputElement>(invalid, onFocus, onBlur)}
      {...props}
    />
  )
);
TextField.displayName = 'TextField';

export type TextAreaFieldProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  invalid?: boolean;
  /** Renders the `used/maxLength` counter under the box; needs `maxLength` to be set. */
  showCounter?: boolean;
};

export const TextAreaField = React.forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  ({ invalid, showCounter, style, onFocus, onBlur, ...props }, ref) => (
    <>
      <textarea
        ref={ref}
        rows={props.rows ?? 4}
        aria-invalid={invalid || undefined}
        style={{
          ...fieldBase,
          minHeight: 104,
          resize: 'vertical',
          padding: '12px 14px',
          fontWeight: 500,
          lineHeight: 1.5,
          borderColor: invalid ? DANGER : BORDER_STRONG,
          ...style,
        }}
        {...focusHandlers<HTMLTextAreaElement>(invalid, onFocus, onBlur)}
        {...props}
      />
      {showCounter && props.maxLength != null && (
        <div
          style={{ textAlign: 'right', fontSize: 12, fontWeight: 600, color: TEXT_3, marginTop: 6 }}
        >
          {String(props.value ?? '').length}/{props.maxLength}
        </div>
      )}
    </>
  )
);
TextAreaField.displayName = 'TextAreaField';

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Colour of the selected pill's text; defaults to brand. */
  color?: string;
}

/**
 * The segmented pill control the scenario/persona forms use for short, fixed choice sets
 * (difficulty, gender, language). Preferred over a dropdown at four options or fewer:
 * every choice stays visible, and it matches the surrounding screens.
 */
export function SegmentedPicker<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  fullWidth,
}: {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  fullWidth?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        gap: 3,
        background: SURFACE,
        padding: 4,
        borderRadius: formTokens.fieldRadius,
        border: `1px solid ${BORDER}`,
        width: fullWidth ? '100%' : 'fit-content',
      }}
    >
      {options.map((opt) => {
        const on = opt.value === value;
        const accent = opt.color ?? BRAND;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(opt.value)}
            style={{
              flex: fullWidth ? '1 1 0' : '0 0 auto',
              padding: '7px 16px',
              fontSize: 12.5,
              fontWeight: 700,
              borderRadius: 9,
              border: 'none',
              cursor: 'pointer',
              background: on ? WHITE : 'transparent',
              color: on ? accent : TEXT_2,
              boxShadow: on ? `0 3px 10px ${brandShadowRgba('toggleShadow', 0.12)}` : 'none',
              transition: 'all .18s ease',
            }}
            onMouseEnter={(e) => {
              if (!on) {
                e.currentTarget.style.background = brandRgba('surfaceCard', 0.6);
                e.currentTarget.style.color = TEXT_1;
              }
            }}
            onMouseLeave={(e) => {
              if (!on) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = TEXT_2;
              }
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A 'YYYY-MM-DD' string and a `Date` are not interchangeable: `new Date('2000-05-20')`
 * parses as UTC midnight, which is still the 19th for any viewer west of UTC. These two
 * helpers keep the value on its intended calendar day by going through local components.
 */
function isoToLocalDate(iso: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function localDateToIso(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export interface DateFieldProps {
  id?: string;
  /** Calendar date as 'YYYY-MM-DD', or '' when unset. */
  value: string;
  onChange: (value: string) => void;
  /** Inclusive bounds as 'YYYY-MM-DD'; also drive the year dropdown's range. */
  min?: string;
  max?: string;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  /** date-fns locale for the calendar's month/day names. */
  locale?: DayPickerProps['locale'];
  /** BCP-47 tag for the button label (e.g. 'vi-VN'); defaults to the runtime locale. */
  displayLocale?: string;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

/**
 * Date picker built on the app's existing Popover + Calendar, skinned to match
 * {@link TextField}. Month and year are dropdowns (`captionLayout="dropdown-buttons"`) —
 * without them, reaching a birth year means clicking the back arrow a few hundred times.
 */
export function DateField({
  id,
  value,
  onChange,
  min,
  max,
  placeholder,
  invalid,
  disabled,
  locale,
  displayLocale,
  'aria-describedby': describedBy,
  'aria-required': ariaRequired,
}: DateFieldProps) {
  const [open, setOpen] = React.useState(false);
  const selected = value ? isoToLocalDate(value) : undefined;
  const fromDate = min ? isoToLocalDate(min) : undefined;
  const toDate = max ? isoToLocalDate(max) : undefined;

  // The locale is the caller's explicit choice, not the reader's language: a date
  // the user types into must be unambiguous. See `formatDateInLocale`.
  const label = selected
    ? formatDateInLocale(selected, displayLocale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          aria-required={ariaRequired}
          style={{
            ...fieldBase,
            height: formTokens.fieldHeight,
            padding: '0 14px',
            borderColor: invalid ? DANGER : open ? BRAND : BORDER_STRONG,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            textAlign: 'left',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.55 : 1,
            color: selected ? TEXT_1 : TEXT_3,
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
          </span>
          <CalendarIcon size={16} style={{ color: TEXT_3, flex: '0 0 auto' }} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" style={{ width: 'auto', padding: 0 }}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? toDate}
          onSelect={(picked) => {
            if (!picked) return;
            onChange(localDateToIso(picked));
            setOpen(false);
          }}
          captionLayout="dropdown-buttons"
          fromDate={fromDate}
          toDate={toDate}
          locale={locale}
          // The shared `calendar.tsx` is the shadcn preset for `captionLayout="buttons"`
          // and react-day-picker's own stylesheet is not imported, so the dropdown layout
          // needs these: without `vhidden` the screen-reader labels ("Month:", "Year:")
          // and the full caption render as visible text stacked over the dropdowns.
          classNames={{
            vhidden: 'sr-only',
            caption: 'flex justify-center pt-1 relative items-center gap-1',
            caption_dropdowns: 'flex items-center gap-1.5',
            dropdown_month: 'relative',
            dropdown_year: 'relative',
            dropdown: 'absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none',
            caption_label:
              'flex items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-sm font-medium',
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export interface PillOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

/**
 * Wrapping rounded pills, as used across the persona-create steps for demographic choices
 * (age band, gender, marital status). Selected pill is filled with the brand gradient.
 * Unlike {@link SegmentedPicker} — a compact 2-3 option toggle inside one tray — this
 * scales to a dozen options that wrap across lines. `clearable` lets a click on the
 * selected pill deselect it, matching the persona form's optional fields.
 */
export function PillGroup<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  clearable,
}: {
  options: ReadonlyArray<PillOption<T>>;
  value: T | null;
  onChange: (value: T | null) => void;
  ariaLabel?: string;
  clearable?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}
    >
      {options.map((opt) => {
        const on = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(on && clearable ? null : opt.value)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '9px 16px',
              borderRadius: 999,
              fontSize: 13.5,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: 'pointer',
              border: on ? 'none' : `1px solid ${BORDER}`,
              background: on ? GRADIENT : WHITE,
              color: on ? WHITE : TEXT_2,
              boxShadow: on ? `0 8px 18px ${brandShadowRgba('brandPrimary', 0.25)}` : 'none',
              transition: 'all .18s ease',
            }}
            onMouseEnter={(e) => {
              if (!on) {
                e.currentTarget.style.borderColor = BORDER_STRONG;
                e.currentTarget.style.color = TEXT_1;
              }
            }}
            onMouseLeave={(e) => {
              if (!on) {
                e.currentTarget.style.borderColor = BORDER;
                e.currentTarget.style.color = TEXT_2;
              }
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The header every full-page create/edit flow opens with: back button, gradient icon tile,
 * title, and an optional badge on the right (the persona flow puts its step counter there).
 */
export function PageHeader({
  icon,
  title,
  onBack,
  backIcon,
  backLabel,
  badge,
  headingLevel = 1,
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
  onBack?: () => void;
  /** Chevron (or any glyph) for the back button — supplied by the caller so this
   *  module stays independent of an icon library. */
  backIcon?: React.ReactNode;
  /** Accessible name for the back button. */
  backLabel?: string;
  badge?: React.ReactNode;
  headingLevel?: 1 | 2;
}) {
  const Heading = headingLevel === 2 ? 'h2' : 'h1';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label={backLabel}
          style={{
            width: 38,
            height: 38,
            borderRadius: 12,
            border: `1px solid ${BORDER}`,
            background: WHITE,
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            flex: '0 0 auto',
            transition: 'border-color .18s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = BORDER_STRONG;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = BORDER;
          }}
        >
          <span style={{ color: TEXT_2, display: 'grid', placeItems: 'center' }}>{backIcon}</span>
        </button>
      )}
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 11,
          background: GRADIENT,
          display: 'grid',
          placeItems: 'center',
          flex: '0 0 auto',
          color: WHITE,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Heading style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>
          {title}
        </Heading>
      </div>
      {badge}
    </div>
  );
}

/** A glass card with an optional section title — the container each form step sits in. */
export function SectionCard({
  title,
  children,
  style,
}: {
  title?: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ ...formTokens.card, padding: 22, ...style }}>
      {title && (
        <div
          style={{
            fontWeight: 800,
            fontSize: 16.5,
            letterSpacing: '-0.01em',
            color: TEXT_1,
            marginBottom: 20,
          }}
        >
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

const buttonBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  height: formTokens.fieldHeight,
  padding: '0 20px',
  borderRadius: formTokens.fieldRadius,
  fontSize: 13.5,
  fontWeight: 700,
  fontFamily: 'inherit',
  transition: 'opacity .18s ease, background .18s ease',
};

export const PrimaryButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ style, disabled, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled}
      style={{
        ...buttonBase,
        border: 'none',
        background: formTokens.gradient,
        color: WHITE,
        boxShadow: `0 6px 16px ${brandShadowRgba('brandPrimary', 0.32)}`,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
      {...props}
    />
  )
);
PrimaryButton.displayName = 'PrimaryButton';

export const GhostButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ style, disabled, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled}
      style={{
        ...buttonBase,
        border: `1px solid ${BORDER}`,
        background: WHITE,
        color: TEXT_2,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
      {...props}
    />
  )
);
GhostButton.displayName = 'GhostButton';

export const DangerButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ style, disabled, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled}
      style={{
        ...buttonBase,
        border: 'none',
        background: DANGER,
        color: WHITE,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
      {...props}
    />
  )
);
DangerButton.displayName = 'DangerButton';

/**
 * Radix `DialogContent` restyled to the form-kit surface. Radix stays underneath, so focus
 * trapping, Esc-to-close and the aria wiring are unchanged — only the skin differs.
 */
export const GlassDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogContent>,
  React.ComponentPropsWithoutRef<typeof DialogContent> & { width?: number }
>(({ style, width = 520, children, ...props }, ref) => (
  <DialogContent
    ref={ref}
    className="gap-0 border-0 p-0 sm:rounded-[20px]"
    style={{
      width: 'calc(100vw - 32px)',
      maxWidth: width,
      maxHeight: 'min(88vh, 780px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: WHITE,
      boxShadow: `0 30px 70px ${brandShadowRgba('textPrimary', 0.28)}`,
      ...style,
    }}
    {...props}
  >
    {children}
  </DialogContent>
));
GlassDialogContent.displayName = 'GlassDialogContent';

export function GlassDialogHeader({
  title,
  description,
  divider = true,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Hide the rule under the header when no body follows it. */
  divider?: boolean;
}) {
  return (
    <DialogHeader
      className="shrink-0 space-y-0 text-left"
      style={{
        padding: '18px 22px 14px',
        borderBottom: divider ? `1px solid ${BORDER}` : undefined,
      }}
    >
      <DialogTitle
        className="text-left"
        style={{ fontSize: 17, fontWeight: 800, color: TEXT_1, letterSpacing: '-0.01em' }}
      >
        {title}
      </DialogTitle>
      {description && (
        <DialogDescription
          className="text-left"
          style={{ fontSize: 13, fontWeight: 500, color: TEXT_2, marginTop: 4 }}
        >
          {description}
        </DialogDescription>
      )}
    </DialogHeader>
  );
}

/** Scrolling middle of a dialog — keeps the header and footer pinned on short viewports. */
export function GlassDialogBody({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        padding: '18px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function GlassDialogFooter({
  children,
  divider = true,
}: {
  children: React.ReactNode;
  /** Hide the rule above the footer when no body precedes it. */
  divider?: boolean;
}) {
  return (
    <div
      style={{
        flex: '0 0 auto',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 10,
        padding: divider ? '14px 22px 18px' : '2px 22px 20px',
        borderTop: divider ? `1px solid ${BORDER}` : undefined,
        background: brandRgba('surfaceCard', 0.96),
      }}
    >
      {children}
    </div>
  );
}

/** Inline error banner used above a form's fields when the submit itself failed. */
export function FormAlert({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        borderRadius: formTokens.fieldRadius,
        border: `1px solid ${brandRgba('danger', 0.3)}`,
        background: brandRgba('danger', 0.06),
        padding: '10px 14px',
        fontSize: 13,
        fontWeight: 600,
        color: brandColors.dangerOnTint,
      }}
    >
      {children}
    </div>
  );
}
